import { useEffect, useState } from 'react';

import { AuthStates } from '@/types/types';

// Cognito session restore (getCurrentUser) is normally a fast local token read,
// so `authState` leaves `loading` almost immediately and this fallback never
// fires on the happy path. It exists purely as a safety net: gating the OTP
// flow on `authState` leaving `loading` must never leave a recipient stuck on
// the loader if a pathological path (e.g. a federated `?code=` return whose Hub
// `signedIn` event never arrives) keeps `authState` at `loading` forever. After
// the timeout we treat auth as resolved so the OTP flow proceeds as it did
// before session gating was introduced.
export const AUTH_RESOLVE_FALLBACK_MS = 8_000;

/**
 * Returns whether the Cognito session-restore attempt has settled.
 *
 * True as soon as `authState` leaves `AuthStates.loading`, or after
 * `AUTH_RESOLVE_FALLBACK_MS` as a deadlock safety net.
 */
const useAuthResolved = (authState: AuthStates): boolean => {
    const resolved = authState !== AuthStates.loading;
    const [fallbackReached, setFallbackReached] = useState(false);

    useEffect(() => {
        if (resolved) {
            return undefined;
        }
        const timer = window.setTimeout(() => setFallbackReached(true), AUTH_RESOLVE_FALLBACK_MS);
        return () => window.clearTimeout(timer);
    }, [resolved]);

    return resolved || fallbackReached;
};

export default useAuthResolved;
